import { useQuery } from '@tanstack/react-query';
import {
  fetchCompetitorSignals,
  fetchEntryStatsByLine,
  fetchOrderBook,
  fetchQuote,
  fetchQuoteHistory,
} from '../../api';
import {
  displayDecimalOdds,
  formatLedgerBookMove,
  formatLedgerImpact,
  hasLedgerImpact,
  signalType,
} from './marketDashboardUtils';

const EMPTY_SIGNAL = {
  t: '—',
  book: 'all',
  type: 'none',
  dx: null,
  dP: null,
  bookMove: '—',
  impact: 'No competitor signal rows returned for this market.',
  hasImpact: false,
};

function n(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

// Number(null) is 0, so depth must reject null before the finite check — otherwise every
// book publishing no order_book reports zero liquidity.
function nullableSize(value) {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function nullableNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function isChartableDecimal(price) {
  const parsed = nullableNumber(price);
  return parsed != null && parsed > 1;
}

function rowIsTwoSided(over, under, stored) {
  if (stored === false) return false;
  if (stored === true) return true;
  if (isChartableDecimal(over) && isChartableDecimal(under)) return true;
  if (isChartableDecimal(over) && !isChartableDecimal(under)) return false;
  return null;
}

function decimalFromProb(p) {
  const prob = n(p, NaN);
  if (!Number.isFinite(prob) || prob <= 0 || prob >= 1) return null;
  return 1 / prob;
}

function safePrice(price, fallbackProb) {
  const parsed = nullableNumber(price);
  if (parsed && parsed > 1) return parsed;
  return decimalFromProb(fallbackProb) || 2;
}

function splitMatchup(label) {
  const parts = String(label || '').split(/\s+v(?:s\.?)?\s+/i);
  return parts.length >= 2 ? [parts[0], parts.slice(1).join(' v ')] : [null, null];
}

function abbr(name) {
  return String(name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .map((part) => part[0]?.toUpperCase())
    .join('') || '?';
}

function fmtKickoff(ts) {
  if (!Number.isFinite(Number(ts))) return '—';
  return new Date(Number(ts) * 1000).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function marketLabel(id) {
  return String(id || 'Market')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function hhmm(ts) {
  if (!Number.isFinite(Number(ts))) return '—';
  return new Date(Number(ts) * 1000).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function buildFixture(quote) {
  const [homeName, awayName] = splitMatchup(quote.team_name);
  const home = homeName || 'Home';
  const away = awayName || 'Away';
  return {
    league: quote.league || '—',
    sport: quote.sport || '—',
    home: { abbr: abbr(home), name: home, color: '#1E3A8A' },
    away: { abbr: abbr(away), name: away, color: '#3B82F6' },
    kickoff: fmtKickoff(quote.fixture_start_time),
    live: { minute: 'Live', period: quote.fixture_status || 'in play', score: { home: '—', away: '—' } },
  };
}

function buildMarket(quote, lineRows) {
  const siblings = lineRows.length
    ? lineRows.map((line) => ({
        label: `${marketLabel(quote.market_id)} · ${line.line ?? '—'}`,
        key: `${quote.market_key}:${line.line ?? ''}`,
        p: (line.true_p_over_pct != null ? n(line.true_p_over_pct) / 100 : quote.p_fair),
      }))
    : [{
        label: `${marketLabel(quote.market_id)} · ${quote.line ?? '—'}`,
        key: quote.market_key,
        p: quote.p_fair,
      }];
  return {
    player: quote.player_name || quote.player_id || '—',
    pos: '—',
    team: quote.team_name || '—',
    market_id: quote.market_id || 'market',
    market_label: marketLabel(quote.market_id),
    line: quote.line,
    market_key: quote.market_key,
    siblings,
  };
}

function buildState(quote, history) {
  const currentP = n(quote.p_fair, 0.5);
  const latestHistory = history?.[history.length - 1];
  const prevHistory = history?.length > 1 ? history[history.length - 2] : null;
  const currentVar = nullableNumber(quote.P) ?? nullableNumber(latestHistory?.P) ?? nullableNumber(latestHistory?.p_variance) ?? n(quote.sd) ** 2;
  const prevP = nullableNumber(prevHistory?.p_fair) ?? currentP;
  const prevVar = nullableNumber(prevHistory?.P) ?? nullableNumber(prevHistory?.p_variance) ?? currentVar;
  const row = {
    p_fair: currentP,
    p_lower: n(quote.p_lower, Math.max(0, currentP - 0.05)),
    p_upper: n(quote.p_upper, Math.min(1, currentP + 0.05)),
    x_logit: nullableNumber(quote.x),
    P: Math.max(0, n(currentVar, 0)),
    marg_over: safePrice(quote.marginated_price || quote.marginated_price_if_gated, currentP),
    marg_under: safePrice(quote.marginated_price_under || quote.marginated_price_under_if_gated, 1 - currentP),
    p_prev: prevP,
    P_prev: Math.max(0, n(prevVar, 0)),
    n_books: n(quote.n_books),
    n_active: n(quote.n_books),
    hold: {
      target: n(quote.margination_floor, 0.05),
      source: 'market quote margination',
      policy: `${quote.sport || 'sport'} · ${quote.market_id || 'market'}`,
      shadeLabel: quote.manual_shaping_active ? 'manual shaping' : 'model margin',
      fixedAdj: { label: quote.quote_two_sided ? 'two-sided quote' : 'one-sided quote', pp: 0 },
    },
  };
  return { pre: row, live: row };
}

function buildPDecomp(quote, signals) {
  const TYPE_META = {
    discovery: { label: 'Discovery pulls', color: 'var(--sig-discovery)' },
    jump: { label: 'Jump (P widen)', color: 'var(--sig-jump)' },
    jump_decay: { label: 'Jump decay', color: 'var(--sig-vig-tight)' },
    dropout: { label: 'Dropout', color: 'var(--sig-dropout)' },
    'vig-wide': { label: 'Vig widen', color: 'var(--sig-vig-wide)' },
    'vig-tight': { label: 'Vig tighten', color: 'var(--sig-vig-tight)' },
    line: { label: 'Line change', color: 'var(--sig-line)' },
    resume: { label: 'Resume', color: 'var(--sig-resume)' },
    other: { label: 'Other signals', color: 'var(--sig-seed)' },
  };
  const buckets = new Map();
  let total = 0;
  for (const s of signals || []) {
    const pBefore = nullableNumber(s.kf_P_before);
    const pAfter = nullableNumber(s.kf_P_after);
    if (pBefore == null || pAfter == null) continue;
    const dP = Math.abs(pAfter - pBefore);
    if (dP < 1e-6) continue;
    const type = signalType(s);
    const key = Object.prototype.hasOwnProperty.call(TYPE_META, type) ? type : 'other';
    buckets.set(key, (buckets.get(key) || 0) + dP);
    total += dP;
  }
  const currentP = Math.max(0, n(nullableNumber(quote.P), 0));
  const entries = [...buckets.entries()].map(([key, weight]) => ({
    src: TYPE_META[key].label,
    weight,
    color: TYPE_META[key].color,
  }));
  if (!entries.length) {
    const fallback = [{ src: 'Baseline P', frac: 1, color: 'var(--sig-seed)' }];
    return { pre: fallback, live: fallback };
  }
  const denom = total || currentP || 1;
  const normalized = entries.map((e) => ({ src: e.src, frac: e.weight / denom, color: e.color }));
  return { pre: normalized, live: normalized };
}

function bookDisplayName(key, fallback) {
  const fb = String(fallback || '').trim();
  if (fb && fb.toLowerCase() !== String(key || '').toLowerCase()) return fb;
  return String(key || '')
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function bookKeyFromRow(name, explicitKey) {
  const key = String(explicitKey || name || '').trim().toLowerCase();
  return key.replace(/\s+/g, '_');
}

// Competitor panel order: the books worth reading first, then everything else
// alphabetically. Keys are the normalized book keys (see _book_key_from_sportsbook).
const BOOK_PANEL_ORDER = [
  'draftkings',
  'fanduel',
  'underdog_fantasy_multipliers_',
  'prizepicks',
  'kalshi',
  'novig',
];

function bookPanelRank(key) {
  const i = BOOK_PANEL_ORDER.indexOf(key);
  return i === -1 ? BOOK_PANEL_ORDER.length : i;
}

function buildBooks(orderBook, signals) {
  // competitor_books = eng.books ref map (same as signaling). Present → live; absent → dropout.
  const lastByBook = new Map();
  const lastSignalByBook = new Map();
  for (const signal of signals || []) {
    const book = signal.sportsbook || signal.book;
    if (!book) continue;
    const key = bookKeyFromRow(book);
    if (!lastByBook.has(key)) lastByBook.set(key, signalType(signal));
    if (!lastSignalByBook.has(key)) lastSignalByBook.set(key, signal);
  }

  const booksByKey = new Map();
  for (const book of orderBook?.competitor_books || []) {
    const key = bookKeyFromRow(book.book, book.book_key);
    const name = bookDisplayName(key, book.book);
    const over = displayDecimalOdds(book.price_over, book.decimal_over);
    const under = isChartableDecimal(book.decimal_under ?? book.price_under)
      ? displayDecimalOdds(book.price_under, book.decimal_under)
      : null;
    const fair = nullableNumber(book.p_fair);
    const twoSided = rowIsTwoSided(over, under, book.is_two_sided) === true;
    const overround = twoSided ? (1 / over) + (1 / under) - 1 : null;
    const last = lastByBook.get(key) || 'none';
    booksByKey.set(key, {
      name,
      line: nullableNumber(book.line),
      side: twoSided ? 'two' : 'one',
      over,
      under,
      fair,
      trust: fair == null ? 0.4 : 0.7,
      R_eff: 0,
      weight: 0,
      vig: overround,
      vigDelta: 0,
      staleness: 0,
      status: book.competitor_muted ? 'muted' : 'live',
      competitorMuted: Boolean(book.competitor_muted),
      // Exchange depth per side. Absent for every book that publishes no order_book, so
      // null means "not published" and must not render as 0.
      sizeOver: nullableSize(book.size_over),
      sizeUnder: nullableSize(book.size_under),
      depthOver: nullableSize(book.depth_over),
      depthUnder: nullableSize(book.depth_under),
      last: last === 'dropout' ? 'none' : last,
    });
  }

  for (const [key, sig] of lastSignalByBook.entries()) {
    const ev = signalType(sig);
    if (ev !== 'dropout' && ev !== 'alt_flick') continue;
    if (booksByKey.has(key)) continue; // ref is live again — do not paint suspended
    const over = displayDecimalOdds(sig.price_over_from, sig.decimal_over_from);
    const under = isChartableDecimal(sig.decimal_under_from ?? sig.price_under_from)
      ? displayDecimalOdds(sig.price_under_from, sig.decimal_under_from)
      : null;
    const twoSided = rowIsTwoSided(over, under, sig.is_two_sided_from ?? sig.is_two_sided_to) === true;
    booksByKey.set(key, {
      name: bookDisplayName(key, sig.sportsbook || sig.book),
      line: nullableNumber(sig.source_line_from ?? sig.source_line_to),
      side: twoSided ? 'two' : over ? 'one' : null,
      over,
      under,
      fair: nullableNumber(sig.p_fair_quote_from),
      trust: 0.35,
      R_eff: 0,
      weight: 0,
      vig: null,
      vigDelta: 0,
      staleness: 0,
      status: 'dropout',
      last: ev,
    });
  }

  // Fuse path: eng.books may be empty until writer syncs panel refs. Fall back to
  // latest entries_fuse / panel / seed row per book so Competitor books shows refs.
  if (booksByKey.size === 0) {
    for (const signal of signals || []) {
      const ev = signalType(signal);
      if (ev !== 'entries_fuse' && ev !== 'panel' && ev !== 'seed') continue;
      const book = signal.sportsbook || signal.book;
      if (!book) continue;
      const key = bookKeyFromRow(book);
      if (booksByKey.has(key)) continue;
      const over = displayDecimalOdds(signal.price_over_to, signal.decimal_over_to);
      if (over == null) continue;
      const under = isChartableDecimal(signal.decimal_under_to ?? signal.price_under_to)
        ? displayDecimalOdds(signal.price_under_to, signal.decimal_under_to)
        : null;
      const twoSided = rowIsTwoSided(over, under, signal.is_two_sided_to) === true;
      const overround = twoSided ? (1 / over) + (1 / under) - 1 : null;
      const fair = nullableNumber(signal.p_fair_quote_to);
      booksByKey.set(key, {
        name: bookDisplayName(key, book),
        line: nullableNumber(signal.source_line_to),
        side: twoSided ? 'two' : 'one',
        over,
        under,
        fair,
        trust: fair == null ? 0.4 : 0.7,
        R_eff: 0,
        weight: 0,
        vig: overround,
        vigDelta: 0,
        staleness: 0,
        status: 'live',
        competitorMuted: false,
        last: ev,
      });
    }
  }

  const books = [...booksByKey.entries()]
    .sort(([ka, a], [kb, b]) => bookPanelRank(ka) - bookPanelRank(kb) || a.name.localeCompare(b.name))
    .map(([, b]) => b);

  const fallback = books.length ? books : [{
    name: 'No current book rows',
    side: null,
    over: null,
    under: null,
    fair: null,
    trust: 0,
    R_eff: 0,
    weight: 0,
    vig: null,
    vigDelta: 0,
    staleness: 0,
    status: 'stale',
    last: 'none',
  }];
  return { pre: fallback, live: fallback };
}

function buildLedger(signals) {
  const rows = (signals || [])
    .filter((s) => {
      const ev = String(s?.event || '').toLowerCase();
      if (ev !== 'no_move') return true;
      const before = nullableNumber(s.kf_p_before);
      const after = nullableNumber(s.kf_p_after);
      const pBefore = nullableNumber(s.kf_P_before);
      const pAfter = nullableNumber(s.kf_P_after);
      const dx = before != null && after != null ? after - before : null;
      const dP = pBefore != null && pAfter != null ? pAfter - pBefore : null;
      return hasLedgerImpact({ dx, dP, gated: s.applied === false, type: signalType(s) });
    })
    .map((s) => {
      const before = nullableNumber(s.kf_p_before);
      const after = nullableNumber(s.kf_p_after);
      const pBefore = nullableNumber(s.kf_P_before);
      const pAfter = nullableNumber(s.kf_P_after);
      const type = signalType(s);
      const dx = before != null && after != null ? after - before : null;
      const dP = pBefore != null && pAfter != null ? pAfter - pBefore : null;
      const gated = s.applied === false;
      const d2 = nullableNumber(s.d2);
      const mad_z = nullableNumber(s.mad_z);
      return {
        t: hhmm(s.timestamp),
        book: s.sportsbook || 'all',
        type,
        dx,
        dP,
        bookMove: formatLedgerBookMove(s, type),
        impact: formatLedgerImpact({ dx, dP, gated, type, d2, mad_z }),
        hasImpact: hasLedgerImpact({ dx, dP, gated, type, d2, mad_z }),
      };
    });
  const ledger = rows.length ? rows : [EMPTY_SIGNAL];
  return { pre: ledger, live: ledger };
}

function buildSeriesFromHistory(history, signals) {
  const points = (history || [])
    .filter((h) => h.p_fair != null)
    .slice(-40);
  const series = (points.length ? points : [{ p_fair: 0.5, p_lower: 0.45, p_upper: 0.55 }]).map((point, index, arr) => ({
    x: arr.length > 1 ? index / (arr.length - 1) : 1,
    p: n(point.p_fair, 0.5),
    lo: n(point.p_lower, Math.max(0, n(point.p_fair, 0.5) - 0.05)),
    hi: n(point.p_upper, Math.min(1, n(point.p_fair, 0.5) + 0.05)),
  }));
  const markers = (signals || []).slice(0, 8).map((signal, index) => ({
    i: Math.max(0, series.length - 1 - index),
    type: signalType(signal),
  }));
  return { series, markers };
}

function buildLiability(quote, entryStats) {
  const rows = entryStats?.lines || [];
  const byLine = new Map();
  for (const row of rows) {
    const key = row.line == null ? quote.line : row.line;
    const current = byLine.get(key) || {
      line: key,
      quote: Math.abs(n(key) - n(quote.line)) < 0.001,
      straight: { lo: 0, lu: 0, n: 0 },
      parlay: { lo: 0, lu: 0, n: 0 },
    };
    // Prefer explicit num_legs splits from the API (1-leg → straight, 2+ → parlay).
    const hasSplit =
      row.liability_net_over_straight != null ||
      row.liability_net_under_straight != null ||
      row.liability_net_over_parlay != null ||
      row.liability_net_under_parlay != null ||
      row.n_entries_straight != null ||
      row.n_entries_parlay != null;
    if (hasSplit) {
      current.straight.lo += n(row.liability_net_over_straight);
      current.straight.lu += n(row.liability_net_under_straight);
      current.straight.n += n(row.n_entries_straight);
      current.parlay.lo += n(row.liability_net_over_parlay);
      current.parlay.lu += n(row.liability_net_under_parlay);
      current.parlay.n += n(row.n_entries_parlay);
    } else {
      // Legacy payload: treat all as straight so the UI still renders.
      current.straight.lo += n(row.liability_net_over);
      current.straight.lu += n(row.liability_net_under);
      current.straight.n += n(row.n_entries);
    }
    byLine.set(key, current);
  }
  if (!byLine.size) {
    byLine.set(quote.line, {
      line: quote.line,
      quote: true,
      straight: { lo: 0, lu: 0, n: 0 },
      parlay: { lo: 0, lu: 0, n: 0 },
    });
  }
  const lines = [...byLine.values()].sort((a, b) => n(a.line) - n(b.line));
  const apiLegDist = Array.isArray(entryStats?.leg_dist) ? entryStats.leg_dist : [];
  const legDist = apiLegDist.length
    ? apiLegDist
        .map((d) => ({ legs: Math.max(1, Math.round(n(d.legs, 1))), n: n(d.n), fee: n(d.fee) }))
        .sort((a, b) => a.legs - b.legs)
    : (() => {
        const totalFee = rows.reduce((sum, row) => sum + n(row.total_fees), 0);
        const totalN = lines.reduce((sum, row) => sum + n(row.straight.n) + n(row.parlay.n), 0);
        return totalN || totalFee ? [{ legs: 1, n: totalN, fee: totalFee }] : [];
      })();
  return {
    lines,
    legDist,
    pairwise: [],
  };
}

async function fetchMarketDashboardPayload(marketKey) {
  const [quote, history, entryStats, competitorSignals, orderBook] = await Promise.all([
    fetchQuote(marketKey),
    fetchQuoteHistory(marketKey, { hours: 3 }),
    fetchEntryStatsByLine(marketKey),
    fetchCompetitorSignals(marketKey, { hours: 6, limit: 50 }),
    fetchOrderBook(marketKey).catch(() => null),
  ]);
  const signals = competitorSignals?.signals || [];
  const series = buildSeriesFromHistory(history, signals);
  return {
    fixture: buildFixture(quote),
    market: buildMarket(quote, entryStats?.lines || []),
    state: buildState(quote, history || []),
    pDecomp: buildPDecomp(quote, signals),
    books: buildBooks(orderBook, signals),
    ledger: buildLedger(signals),
    liability: buildLiability(quote, entryStats),
    seriesByMode: { pre: series, live: series },
  };
}

export const marketDashboardKeys = {
  detail: (marketKey) => ['marketDashboard', marketKey || ''],
};

export function useMarketDashboard(marketKey, options = {}) {
  return useQuery({
    queryKey: marketDashboardKeys.detail(marketKey),
    queryFn: () => fetchMarketDashboardPayload(marketKey),
    enabled: !!marketKey,
    staleTime: 2_000,
    refetchInterval: 5_000,
    ...options,
  });
}
